import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import CompaniesService from 'api/companies/CompaniesService';
import UsersService from 'api/users/UsersService';
import { SelectValueLabelDto } from 'api/common/models/SelectValueLabelDto';
import Button from 'common/components/button/Button';
import CheckBox from 'common/components/checkBox/CheckBox';
import InputSearch from 'common/components/inputSearch/InputSearch';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Modal from 'common/components/modal/Modal';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE, MAX_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import styles from './ExportCompaniesUsersModal.module.scss';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

const normalize = (s: string) => (s || '').toLowerCase().trim();

// Altura FIXA → o modal não salta de tamanho ao pesquisar (a tabela é que rola por dentro).
const MODAL_STYLE = {
    content: {
        position: 'relative' as const,
        inset: 'auto',
        width: 'min(560px, 96vw)',
        height: 'min(680px, 90vh)',
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
    },
};

const ExportCompaniesUsersModal = ({ isOpen, onClose }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const [companies, setCompanies] = useState<SelectValueLabelDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setSearch('');
        setSelected(new Set());
        const load = async () => {
            try {
                setIsLoading(true);
                // O endpoint já força active = true e from_system = false; a ordenação só é
                // aplicada quando orderColumn vem preenchido.
                const result = await CompaniesService.catalog({
                    page: 1,
                    itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
                    orderColumn: 'c.name',
                    orderBy: 'asc',
                });
                if (cancelled) return;
                setCompanies([...(result || [])].sort((a, b) => (a.label || '').localeCompare(b.label || '')));
            } catch (error) {
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get companies catalog for users export`, error);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [isOpen]);

    const filteredCompanies = useMemo(() => {
        const q = normalize(search);
        if (!q) return companies;
        return companies.filter(c => normalize(c.label || '').includes(q));
    }, [companies, search]);

    const filteredIds = useMemo(() => filteredCompanies.map(c => c.value), [filteredCompanies]);

    const toggleCompany = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Marca/desmarca em bloco — só as empresas atualmente filtradas.
    const toggleMany = (ids: string[]) => {
        setSelected(prev => {
            const next = new Set(prev);
            const allIn = ids.length > 0 && ids.every(id => next.has(id));
            ids.forEach(id => { if (allIn) next.delete(id); else next.add(id); });
            return next;
        });
    };

    const isAllSelected = (ids: string[]) => ids.length > 0 && ids.every(id => selected.has(id));

    const confirm = async () => {
        if (selected.size === 0) return;
        try {
            setIsExporting(true);
            // O ficheiro é gerado no backend; aqui só se guarda o blob.
            const report = await UsersService.exportByCompanies(Array.from(selected));
            // 204 sem corpo = as empresas selecionadas não têm utilizadores ativos.
            if (!report || report.size === 0) {
                addToast(t('companies.export_users.no_users') as string, { appearance: 'warning' });
                return;
            }
            saveAs(report, `${t('users.title')}_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.xlsx`);
            onClose();
        } catch (error) {
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't export companies users`, error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={MODAL_STYLE}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.title}>{t('companies.export_users.title')}</div>
                    <div className={styles.subtitle}>{t('companies.export_users.subtitle')}</div>
                </div>

                <div className={styles.body}>
                    <div className={styles.searchRow}>
                        <InputSearch placeholder={t('companies.export_users.search') as string} onChangeValue={setSearch} />
                    </div>

                    <div className={styles.tableWrapper}>
                        {isLoading ? (
                            <div className={styles.center}><LoadingSpinner /></div>
                        ) : filteredCompanies.length === 0 ? (
                            <div className={styles.empty}>{t('companies.export_users.no_companies')}</div>
                        ) : (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th className={styles.checkCol}>
                                            <CheckBox checked={isAllSelected(filteredIds)} onChange={() => toggleMany(filteredIds)} />
                                        </th>
                                        <th>{t('companies.list.name')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCompanies.map(c => (
                                        <tr key={c.value} onClick={() => toggleCompany(c.value)}>
                                            <td className={styles.checkCol} onClick={e => e.stopPropagation()}>
                                                <CheckBox checked={selected.has(c.value)} onChange={() => toggleCompany(c.value)} />
                                            </td>
                                            <td className={styles.name}>{c.label}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerInfo}>
                        {selected.size > 0 && <strong>{t('companies.export_users.selected', { count: selected.size })}</strong>}
                    </div>
                    <div className={styles.footerButtons}>
                        <Button preset="secondary" text={t('common.cancel') as string} onClick={onClose} />
                        <Button
                            text={t('companies.export_users.confirm') as string}
                            onClick={confirm}
                            isDisabled={selected.size === 0}
                            isLoading={isExporting}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ExportCompaniesUsersModal;
